import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { isNumeric } from 'utils/string';

const CustomizedTableEditor = React.forwardRef(({ data, columns, onDataChange }, ref) => {
  const tableRef = useRef(null);
  const tabulatorRef = useRef(null);
  const [localData, setLocalData] = useState(data);

  // Update local data when parent data changes
  useEffect(() => {
    setLocalData(data);
    if (tabulatorRef.current) {
      tabulatorRef.current.setData(data);
    }
  }, [data]);

  // Add CSS styles
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/tabulator-tables@5.5.2/dist/css/tabulator.min.css';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      .tabulator-container {
        height: 100%;
      }
      .tabulator {
        height: 100%;
        max-height: unset !important;
        font-size: 14px;
      }
      .tabulator .tabulator-tableholder {
        overflow-y: auto;
        overflow-x: auto;
      }
      .tabulator .tabulator-header {
        background-color: #f5f5f5;
      }
      .tabulator .tabulator-header .tabulator-col {
        height: 80px !important;
        background-color: #f5f5f5;
        border-right: 1px solid #ddd;
      }
      .tabulator .tabulator-header .tabulator-col .tabulator-col-content {
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .tabulator .tabulator-header .tabulator-col .tabulator-col-title {
        white-space: pre-wrap;
        line-height: 1.2;
        text-align: center;
        font-weight: 500;
        font-size: 12px;
        word-break: break-word;
        width: 100%;
      }
      .tabulator-row .tabulator-cell {
        padding: 8px;
        border-right: 1px solid #eee;
        text-align: center;
      }
      .tabulator-row .tabulator-cell input {
        text-align: center;
      }
      .tabulator input:focus {
        outline: 2px solid #1890ff;
        border-radius: 2px;
      }
      .tabulator-row.tabulator-selected {
        background-color: #e6f7ff !important;
      }
      .tabulator-row.tabulator-selected:hover {
        background-color: #bae7ff !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    
    if (!tabulatorRef.current) return;

    const clipboardData = e.clipboardData.getData('text');
    const rows = clipboardData.split(/\r?\n/).filter(row => row.trim());
    const pastedData = rows.map(row => row.split('\t').map(cell => cell.trim()));
    
    const totalRows = data.length;
    const selectedRows = tabulatorRef.current.getSelectedRows();
    
    // Get editable columns in correct order
    const editableColumns = columns
      .filter(col => col.editable)
      .map(col => col.field);

    // Validate row count
    if (pastedData.length > totalRows) {
      alert(`Cannot paste ${pastedData.length} rows. Maximum allowed rows is ${totalRows}`);
      return;
    }

    // Validate column count
    if (pastedData[0].length !== editableColumns.length) {
      alert(`Must paste exactly ${editableColumns.length} columns`);
      return;
    }

    if (selectedRows.length > 0) {
      const allRows = tabulatorRef.current.getRows();
      const unselectedRows = allRows.filter(row => !selectedRows.includes(row));
      
      if (pastedData.length <= selectedRows.length) {
        pastedData.forEach((rowData, index) => {
          const updateData = {};
          rowData.forEach((value, colIndex) => {
            const field = editableColumns[colIndex];
            updateData[field] = isNumeric(value) ? parseFloat(value) : value;
          });
          selectedRows[index].update(updateData);
        });
      } else {
        selectedRows.forEach((row, index) => {
          const rowData = pastedData[index];
          const updateData = {};
          rowData.forEach((value, colIndex) => {
            const field = editableColumns[colIndex];
            updateData[field] = isNumeric(value) ? parseFloat(value) : value;
          });
          row.update(updateData);
        });

        const remainingData = pastedData.slice(selectedRows.length);
        remainingData.forEach((rowData, index) => {
          if (index < unselectedRows.length) {
            const updateData = {};
            rowData.forEach((value, colIndex) => {
              const field = editableColumns[colIndex];
              updateData[field] = isNumeric(value) ? parseFloat(value) : value;
            });
            unselectedRows[index].update(updateData);
          }
        });
      }
    } else {
      const allRows = tabulatorRef.current.getRows();
      pastedData.forEach((rowData, index) => {
        const updateData = {};
        rowData.forEach((value, colIndex) => {
          const field = editableColumns[colIndex];
          updateData[field] = isNumeric(value) ? parseFloat(value) : value;
        });
        allRows[index].update(updateData);
      });
    }
  }, [data.length, columns]);

  const handleCopy = useCallback((e) => {
    if (e.ctrlKey && e.key === 'c' && tabulatorRef.current) {
      const selectedData = tabulatorRef.current.getSelectedData();
      if (selectedData.length) {
        const copyText = selectedData.map(row => 
          columns
            .filter(col => col.editable)
            .map(col => row[col.field])
            .join('\t')
        ).join('\n');
        
        navigator.clipboard.writeText(copyText);
      }
    }
  }, [columns]);

  useEffect(() => {
    if (tableRef.current && !tabulatorRef.current) {
      tabulatorRef.current = new Tabulator(tableRef.current, {
        data: data,
        columns: [
          {
            formatter: "rowSelection", 
            titleFormatter: "rowSelection", 
            titleFormatterParams: {
              rowRange: "active"
            },
            hozAlign: "center",
            headerSort: false,
            width: 40,
            frozen: true
          },
          {
            title: "Stage\n#",
            field: "stageNumber",
            headerSort: false,
            width: 60,
            frozen: true,
            editor: false,
            headerHozAlign: "center",
            hozAlign: "center",
            formatter: function(cell) {
              return cell.getRow().getPosition(true);
            }
          },
          ...columns.map(col => {
            if (col.field === 'kic_name') {
              console.log('KIc name column config:', col);
              console.log('KIc name values:', col.editorParams?.values);
              return {
                ...col,
                editor: function(cell) {
                  const row = cell.getRow();
                  const shouldShow = row.getData().k_ic_reference;
                  console.log('Should show KIc dropdown:', shouldShow);
                  
                  if (!shouldShow) return false;

                  // Create a select element
                  const select = document.createElement("select");
                  select.style.cssText = `
                    width: 100%;
                    color: #000;           /* Set text color */
                    background: #fff;      /* Set background color */
                    padding: 2px 4px;      /* Add some padding */
                    border: 1px solid #d9d9d9;  /* Add border */
                    border-radius: 2px;    /* Round corners */
                    font-size: 14px;       /* Set font size */
                  `;
                  
                  // Add options
                  const values = col.editorParams?.values || [];
                  values.forEach(value => {
                    const option = document.createElement("option");
                    option.value = value;
                    option.text = value;
                    option.style.color = "#000";
                    select.appendChild(option);
                  });

                  // Set current value
                  select.value = cell.getValue() || '';

                  // Add change event listener
                  select.addEventListener('change', (e) => {
                    cell.setValue(e.target.value);
                    // Trigger dataChanged event
                    if (tabulatorRef.current) {
                      const updatedData = tabulatorRef.current.getData();
                      setLocalData(updatedData);
                      onDataChange?.(updatedData);
                    }
                  });

                  return select;
                },
                formatter: function(cell) {
                  const row = cell.getRow();
                  return row.getData().k_ic_reference ? cell.getValue() : '';
                },
                width: 120,
                headerHozAlign: "center",
                hozAlign: "center"
              };
            }
            if (col.field === 'fracture_toughness') {
              return {
                ...col,
                editor: function(cell) {
                  // Only editable if k_ic_reference is false
                  const row = cell.getRow();
                  return !row.getData().k_ic_reference ? "input" : false;
                },
                formatter: function(cell) {
                  const row = cell.getRow();
                  const value = cell.getValue();
                  
                  // Add visual indication that it's calculated
                  if (row.getData().k_ic_reference) {
                    return `<div style="color: #666; font-style: italic;">${value || 'Calculated'}</div>`;
                  }
                  return value;
                },
                validator: ["numeric"],
                width: 120,
                headerHozAlign: "center",
                hozAlign: "right"
              };
            }
            return {
              ...col,
              editor: col.editable ? "input" : false,
              validator: col.editable ? ["required", "numeric"] : [],
              width: 120,
              headerHozAlign: "center",
              hozAlign: "right"
            };
          })
        ],
        layout: "fitData",
        height: "100%",
        selectable: true,
        selectableRangeMode: "click",
        clipboard: false,
        keybindings: {
          "copy": false,
          "paste": false
        },
        dataChanged: function() {
          const updatedData = tabulatorRef.current.getData();
          setLocalData(updatedData);
          onDataChange?.(updatedData);
        },
        cellClick: function(e, cell) {
          const newValue = !cell.getValue();
          cell.setValue(newValue);
          
          const row = cell.getRow();
          if (!newValue) {
            // When unchecked, clear both kic_name and fracture_toughness
            row.update({ 
              kic_name: '',
              fracture_toughness: '' 
            });
          } else {
            // When checked, clear fracture_toughness as it will be calculated
            row.update({ 
              fracture_toughness: '' 
            });
          }
          
          // Force table redraw to update select visibility
          cell.getTable().redraw(true);
        }
      });

      const pasteFn = (e) => {
        e.stopPropagation();
        handlePaste(e);
      };

      tabulatorRef.current.element.addEventListener('paste', pasteFn);
      document.addEventListener('keydown', handleCopy);

      return () => {
        if (tabulatorRef.current) {
          tabulatorRef.current.element.removeEventListener('paste', pasteFn);
          document.removeEventListener('keydown', handleCopy);
          tabulatorRef.current.destroy();
          tabulatorRef.current = null;
        }
      };
    }
  }, [columns, handlePaste, handleCopy, data, onDataChange]);

  React.useImperativeHandle(ref, () => ({
    getCurrentData: () => {
      return tabulatorRef.current ? tabulatorRef.current.getData() : localData;
    }
  }));

  return (
    <div className="tabulator-container">
      <div ref={tableRef} />
    </div>
  );
});

export default CustomizedTableEditor; 